import isEqual from "fast-deep-equal"
import ApplicationComponent from "legacy/ApplicationComponent"
import { debounce, isEmpty, parseJSONError } from "legacy/helpers.es6"
import PosItemList from "legacy/sales/PosItemList"
import SaleCheckout from "legacy/sales/SaleCheckout"
import PropTypes from "prop-types"
import React from "react"

// Note: jQuery ($) is loaded globally
// Note: Amplitude is loaded globally

class CreateSaleContainer extends ApplicationComponent {
  constructor(args) {
    super(args)
    this.state = {
      processingCreate: false,
      loadingPrices: false,
      pricingError: null,
      createError: null,
      mobileView: "posItems",
      cart: this.emptyCart(),
      receiptPrinterId: null,
      searchTerm: this.props.searchTerm,
      filteredItems: this.validItems(),
      fetchedPricing: {
        posItems: [],
        totalPrice: 0,
        subtotal: 0,
        taxAmount: 0,
      },
    }
    this.handleAddToCart = this.handleAddToCart.bind(this)
    this.handleRemoveFromCart = this.handleRemoveFromCart.bind(this)
    this.handleUpdateItemInCart = this.handleUpdateItemInCart.bind(this)
    this.handleUpdateCustomer = this.handleUpdateCustomer.bind(this)
    this.handleUpdatePaymentMethod = this.handleUpdatePaymentMethod.bind(this)
    this.handleUpdateReceiptPrinterId =
      this.handleUpdateReceiptPrinterId.bind(this)
    this.handleLoading = this.handleLoading.bind(this)
    this.createSale = this.createSale.bind(this)
    this.preparePosItem = this.preparePosItem.bind(this)
    this.handleSearchInputChange = this.handleSearchInputChange.bind(this)
    this.filterItems = this.filterItems.bind(this)
    this.isGuest = this.isGuest.bind(this)
    this.handleUpdateNote = this.handleUpdateNote.bind(this)
    this.handleRetry = this.handleRetry.bind(this)
    this.prepareToFetchCartPricing = this.prepareToFetchCartPricing.bind(this)
    this.fetchCartPricing = debounce(this.fetchCartPricing.bind(this), 750)
    this.lastPriceCalculateRequest = null
  }

  componentDidMount() {
    this.props.handleUpdateSearchTerm()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.searchTerm !== this.props.searchTerm) {
      this.handleSearchInputChange(this.props.searchTerm)
    }
  }

  toggleMobileView() {
    if (this.props.isMobile()) {
      if (this.state.mobileView === "posItems") {
        this.setState({ mobileView: "checkout" })
      } else {
        this.setState({ mobileView: "posItems" })
      }
      return this.state.mobileView
    }
  }

  handleAddToCart(item) {
    if (this.state.cart.items.length < 1) {
      Amplitude.trackEvent("web_pos:create_add_items")
    }
    this.setState(
      { cart: { ...this.state.cart, items: [...this.state.cart.items, item] } },
      () => {
        this.prepareToFetchCartPricing(item)
      }
    )
  }

  handleRemoveFromCart(item) {
    this.setState(
      {
        cart: {
          ...this.state.cart,
          items: this.state.cart.items.filter(
            (cartItem) => cartItem.id !== item.id
          ),
        },
      },
      () => {
        this.prepareToFetchCartPricing(item)
      }
    )
  }

  handleUpdateItemInCart(item) {
    const index = this.state.cart.items.findIndex(
      (cartItem) => cartItem.id === item.id
    )
    const items = this.state.cart.items
    items[index] = item
    this.setState({ cart: { ...this.state.cart, items } }, () => {
      this.prepareToFetchCartPricing(item)
    })
  }

  handleUpdateCustomer(customer) {
    this.setState({ cart: { ...this.state.cart, customer } })
  }

  handleUpdateNote(note) {
    this.setState({ cart: { ...this.state.cart, note } })
  }

  handleUpdatePaymentMethod(paymentMethod) {
    this.setState({
      cart: { ...this.state.cart, paymentMethod },
    })
  }

  handleUpdateReceiptPrinterId(receiptPrinterId) {
    this.setState({ receiptPrinterId })
  }

  handleLoading(loading) {
    this.setState({ processingCreate: loading })
  }

  handleRetry() {
    this.setState({ pricingError: false })
    this.prepareToFetchCartPricing()
  }

  isGuest() {
    const customer = this.state.cart.customer
    return customer && !customer.encodedId && customer.name === "Guest"
  }

  prepareToFetchCartPricing(item) {
    if (isEmpty(this.state.cart.items)) {
      this.setState({
        fetchedPricing: {
          posItems: [],
          totalPrice: 0,
          subtotal: 0,
          taxAmount: 0,
        },
        loadingPrices: false,
        pricingError: null,
      })
      return
    }

    if (item && !this.state.loadingPrices) {
      this.setState({ loadingPrices: true }, () => {
        this.lastPriceCalculateRequest = this.fetchCartPricing()
      })
    } else {
      this.lastPriceCalculateRequest = this.fetchCartPricing()
    }
  }

  itemDataFromCurrentCart() {
    return this.state.cart.items.map((item) => {
      return {
        id: item.id,
        quantity: item.quantity,
        item_price: item.unitPrice,
        tax_rate: item.taxRate / 100,
        discount_ids: (item.discounts || [])
          .map((discount) => discount && discount.encodedId)
          .filter(Boolean),
      }
    })
  }

  fetchCartPricing() {
    const params = this.itemDataFromCurrentCart()
    return $.ajax({
      url: $context.calculatePriceEndpoint,
      type: "POST",
      data: JSON.stringify({ pos_items: params }),
      dataType: "json",
      contentType: "application/json",
      beforeSend: () => {
        if (this.lastPriceCalculateRequest) {
          this.lastPriceCalculateRequest.abort()
        }
      },
      success: (data) => {
        // compare current state of cart to response to see if response is applicable (solves for async race condition)
        const currentCart = this.itemDataFromCurrentCart()
        if (isEqual(currentCart, params)) {
          this.setState({
            fetchedPricing: data,
            pricingError: null,
            loadingPrices: false,
          })
        }
      },
      error: (data) => {
        if (data.statusText === "abort") return
        this.setState({
          pricingError: parseJSONError(data.responseText),
          loadingPrices: false,
        })
      },
    })
  }

  createSale() {
    const itemParams = this.itemDataFromCurrentCart()
    const paymentParams = {
      payment_type: this.state.cart.paymentMethod.type,
    }
    if (this.state.cart.paymentMethod.type === "credit") {
      if (this.state.cart.paymentMethod.cardId === "add_new_card") {
        paymentParams.stripe_card_token =
          this.state.cart.paymentMethod.stripeToken
      } else {
        paymentParams.stripe_card_id = this.state.cart.paymentMethod.cardId
      }
    } else if (this.state.cart.paymentMethod.type === "check") {
      paymentParams.check_number = this.state.cart.paymentMethod.checkNumber
    }

    const params = {
      ...paymentParams,
      pos_items: itemParams,
      discount_ids: [],
      note: this.state.cart.note,
    }
    if (this.state.cart.customer.id) {
      params.contact_id = this.state.cart.customer.id
    } else if (this.state.cart.customer.email) {
      // this is a guest transaction with an email attached!
      params.contact = { email: this.state.cart.customer.email }
    }
    // by sending a printer_id, the server will print a receipt to that printer
    if (this.state.receiptPrinterId !== "none") {
      params.printer_id = this.state.receiptPrinterId
    }

    this.setState({ processingCreate: true })
    $.ajax({
      url: $context.salesEndpoint,
      data: JSON.stringify(params),
      type: "POST",
      dataType: "json",
      contentType: "application/json",
      success: (data) => {
        Amplitude.trackEvent("web_pos:create_complete", {
          sale_id: data.id,
          item_count: this.state.cart.items.length,
          guest_checkout: this.isGuest(),
        })
        this.setState({ processingCreate: false })
        this.props.handleSaleCreated(data)
      },
      error: (data) => {
        this.setState({
          createError: parseJSONError(data.responseText),
          processingCreate: false,
        })
      },
    })
  }

  emptyCart() {
    return {
      items: [],
      customer: null,
      note: "",
      paymentMethod: {
        type: "credit",
        cardId: "add_new_card",
      },
    }
  }

  renderResetCartButton() {
    return (
      <button
        className="btn btn-secondary"
        type="button"
        onClick={() =>
          this.setState({
            cart: this.emptyCart(),
            fetchedPricing: {
              posItems: [],
              totalPrice: 0,
              subtotal: 0,
              taxAmount: 0,
            },
            loadingPrices: false,
            pricingError: null,
          })
        }
      >
        Reset Cart
      </button>
    )
  }

  validItems() {
    return this.props.posItems.map(this.preparePosItem).filter(Boolean)
  }

  preparePosItem(template) {
    if (template.frequency !== "one_time") return null
    if (template.category === "metered_electric") return null
    if (!template.unitPrice) return null
    if ([NaN, 0].includes(parseFloat(template.unitPrice))) return null

    const item = { ...template }
    item.taxRate = item.taxRate || 0.0
    item.unitPrice = parseFloat(item.unitPrice) * 100
    return item
  }

  filterItems(items, searchTerm) {
    const itemNameFilter = (item) => {
      if (!item || !item.name) return false
      return item.name.toLowerCase().includes(searchTerm)
    }
    return items.filter(itemNameFilter)
  }

  handleSearchInputChange(term) {
    const filteredItems = this.filterItems(
      this.validItems(),
      term.toLowerCase()
    )
    this.setState({ searchTerm: term, filteredItems })
  }

  render() {
    return (
      <div className="grid grid-cols-12 gap-3">
        <div
          style={{ marginTop: !this.props.isMobile() ? "-52px" : "" }}
          className={`col-span-12 md:col-span-8 md:inline ${
            this.state.mobileView === "checkout" ? "hidden" : ""
          }`}
        >
          <div className="mb-3 hidden justify-end md:flex">
            <div className="relative">{this.renderResetCartButton()}</div>
          </div>
          <PosItemList
            posItems={this.state.filteredItems}
            categories={this.props.categories}
            handleAddToCart={this.handleAddToCart}
            handleRemoveFromCart={this.handleRemoveFromCart}
            cart={this.state.cart}
            renderTaxRate={this.props.renderTaxRate}
            listedPumpPriceLastUpdatedAt={
              this.props.listedPumpPriceLastUpdatedAt
            }
          />
          <div className="paper-page sticky bottom-0 flex justify-center space-x-2 p-2 md:hidden">
            {this.renderResetCartButton()}
            <button
              type="button"
              onClick={() => this.toggleMobileView()}
              className="btn btn-primary px-8"
            >
              Continue to checkout
            </button>
          </div>
        </div>
        <div
          className={`card col-span-12 p-4 pt-0 md:col-span-4 md:inline ${
            this.state.mobileView === "posItems" ? "hidden" : ""
          }`}
        >
          <div className="inline flex justify-between md:hidden">
            <button
              type="button"
              className="btn bg-white"
              onClick={() => this.toggleMobileView()}
            >
              <i className="fa-kit fa-sf-chevron-left pr-2" />
              Return to POS Items
            </button>
          </div>
          <SaleCheckout
            cart={this.state.cart}
            handleUpdateItemInCart={this.handleUpdateItemInCart}
            handleRemoveFromCart={this.handleRemoveFromCart}
            handleUpdateCustomer={this.handleUpdateCustomer}
            handleUpdateNote={this.handleUpdateNote}
            handleUpdatePaymentMethod={this.handleUpdatePaymentMethod}
            handleUpdateReceiptPrinterId={this.handleUpdateReceiptPrinterId}
            handleCheckout={this.createSale}
            handleLoading={this.handleLoading}
            handleRetry={this.handleRetry}
            note={this.state.cart.note}
            receiptPrinterId={this.state.receiptPrinterId}
            isGuest={this.isGuest}
            processing={this.state.processingCreate}
            loadingPrices={this.state.loadingPrices}
            createError={this.state.createError}
            pricingError={this.state.pricingError}
            fetchedPricing={this.state.fetchedPricing}
          />
        </div>
      </div>
    )
  }
}

CreateSaleContainer.propTypes = {
  posItems: PropTypes.array.isRequired,
  categories: PropTypes.array.isRequired,
  handleUpdateSearchTerm: PropTypes.func.isRequired,
  handleSaleCreated: PropTypes.func.isRequired,
  isMobile: PropTypes.func.isRequired,
  searchTerm: PropTypes.string,
  renderTaxRate: PropTypes.func,
  listedPumpPriceLastUpdatedAt: PropTypes.string,
}

export default CreateSaleContainer
